import React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './LoadingState';
import './primitives.css';

const toneVar = {
  default: 'var(--text-primary)',
  accent: 'var(--accent-from)',
  critical: 'var(--state-critical)',
  high: 'var(--state-high)',
  medium: 'var(--state-medium)',
  low: 'var(--state-low)',
  success: 'var(--state-success)',
  warning: 'var(--state-warning)',
  info: 'var(--state-info)',
};

/**
 * One KPI. The tone color is applied only when the value is non-zero, so a
 * fresh account doesn't show red zeros: zero / empty / '-' render neutral.
 *
 *   <StatCard label="Critical" value={3} tone="critical" icon={AlertTriangle} />
 *   <StatCard label="Passwords exposed" value={0} tone="critical" hint="Add a domain to start monitoring" />
 *
 *   loading: shows a skeleton instead of the value
 *   error:   shows "Unavailable" instead of a misleading number
 *   delta / hint: one line of secondary text under the value
 */
export default function StatCard({
  label,
  value,
  delta,
  hint,
  tone = 'default',
  icon: Icon,
  loading = false,
  error = false,
  className = '',
}) {
  const numeric = typeof value === 'number' ? value : Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  const isZero = value == null || value === '' || value === '-' || (Number.isFinite(numeric) && numeric === 0);
  const color = isZero || error ? 'var(--text-secondary)' : toneVar[tone] || toneVar.default;
  const sub = delta ?? hint;

  return (
    <div className={cn('is-card', className)} style={{ padding: 'var(--space-5)' }} data-testid="stat-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="is-stat-label truncate">{label}</p>
          {loading ? (
            <Skeleton width={80} height={28} style={{ marginTop: 8 }} />
          ) : error ? (
            <p className="is-stat-value" style={{ fontSize: 'var(--fs-base)', lineHeight: '34px', color }}>
              Unavailable
            </p>
          ) : (
            <p className="is-stat-value" style={{ color }}>{value ?? '-'}</p>
          )}
          {sub != null && !loading && (
            <p className="t-small" style={{ marginTop: 4 }}>{sub}</p>
          )}
        </div>
        {Icon && (
          <div className="is-stat-icon" style={isZero ? undefined : { color }} aria-hidden="true">
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}
